import { dropItemCurrencySymbol } from "../normalize";
import type { DropItem } from "../types";

/**
 * WHAT THE ITEMS SAY THAT THE DROP DOES NOT.
 *
 * The drop payload carries no supply (and an `items_count` of 0 even beside
 * ten items — a backend gap) and no price, which is where the legacy "TBA" and
 * "unavailable" came from. Every item states its own quantity and price, so:
 *  - supply is the sum of the items' quantities — only when EVERY item states one;
 *  - price is the items' range, in their own currency, when they share one;
 *    when they do not, the lowest price in each currency ("from 0.2 USDT · 1 POL").
 * Otherwise the figure stays unknown rather than half-counted.
 */
export function fromItems(items: DropItem[]) {
  const qty = items.map((i) => i.meta_data?.quantity ?? i.meta_data?.count_nft ?? null);
  const supply = items.length > 0 && qty.every((q) => typeof q === "number" && q > 0) ? (qty as number[]).reduce((a, b) => a + b, 0) : null;
  const priced = items.filter((i) => typeof i.meta_data?.price === "number");
  const symbols = new Set(priced.map((i) => dropItemCurrencySymbol(i)).filter(Boolean));
  const prices = priced.map((i) => i.meta_data.price as number);
  const range = priced.length === items.length && items.length > 0 && symbols.size === 1
    ? { min: Math.min(...prices), max: Math.max(...prices), symbol: [...symbols][0] as string }
    : null;
  // Mixed currencies: one "from" figure per currency, never a sum across them.
  const byCurrency = new Map<string, number>();
  for (const i of priced) {
    const sym = dropItemCurrencySymbol(i);
    if (!sym) continue;
    const price = i.meta_data.price as number;
    byCurrency.set(sym, Math.min(price, byCurrency.get(sym) ?? Infinity));
  }
  const lowest = !range && priced.length === items.length && byCurrency.size > 1
    ? [...byCurrency].map(([symbol, min]) => ({ symbol, min }))
    : null;
  return { supply, range, lowest };
}

type Range = { min: number; max: number; symbol: string } | null;
type Lowest = { symbol: string; min: number }[] | null;

/** A price summary for a tile or a plate, as parts (each its own isolated run, so RTL never reorders a number and its ticker): one currency → [{ "10" | "5–20", USDT }]; several → the lowest in each. */
export function pricePartsOf(range: Range, lowest: Lowest, nf: Intl.NumberFormat): { text: string; symbol: string }[] | null {
  if (range) return [{ text: range.min === range.max ? nf.format(range.min) : `${nf.format(range.min)}–${nf.format(range.max)}`, symbol: range.symbol }];
  if (lowest) return lowest.map((l) => ({ text: nf.format(l.min), symbol: l.symbol }));
  return null;
}

